import { useState, useCallback } from 'react'
import { calendlyService } from '../../../../../services/calendlyService'
import toast from 'react-hot-toast'
import type {
  AppointmentState,
  ManualAppointmentData,
  UserInfo,
} from '../types'

export const useAppointmentData = () => {
  const [appointmentData, setAppointmentData] =
    useState<AppointmentState | null>(null)
  const [isPollingData, setIsPollingData] = useState(false)

  const createManualAppointment = useCallback(
    async (appointmentData: ManualAppointmentData) => {
      try {
        console.log('🔧 Creando cita manual:', appointmentData)

        const response = await fetch('/api/calendly/manual-appointment', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(appointmentData),
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const result = await response.json()
        console.log('✅ Cita manual creada:', result)
        return result
      } catch (error) {
        console.error('❌ Error creando cita manual:', error)
        throw error
      }
    },
    []
  )

  const fetchAppointmentData = useCallback(
    async (
      email: string,
      userInfo: UserInfo,
      onComplete?: (data: any) => Promise<void>,
      maxAttempts = 5
    ) => {
      console.log('🔄 Obteniendo datos de la cita...')

      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          console.log(
            `🔍 Intento ${attempt}/${maxAttempts} - Buscando citas para ${email}`
          )

          // Wait before each attempt (except the first)
          if (attempt > 1) {
            await new Promise(resolve => setTimeout(resolve, 3000))
          }

          // const response = await calendlyService.getAppointmentsByEmail(email)
          const response = await calendlyService.getUserAppointments()
          console.log('📋 Citas encontradas:', response)

          if (response.success && response.data && response.data.count > 0) {
            // Find the most recent appointment
            const recentAppointments = response.data.data
              .filter(apt => apt.status === 'scheduled')
              .sort(
                (a, b) =>
                  new Date(b.createdAt).getTime() -
                  new Date(a.createdAt).getTime()
              )

            console.log('recentappointments', recentAppointments)

            if (recentAppointments.length > 0) {
              const latestAppointment = recentAppointments[0]
              console.log('✅ Cita más reciente encontrada:', latestAppointment)

              // Check if it's recent (last 10 minutes)
              const appointmentTime = new Date(latestAppointment.createdAt)
              const now = new Date()
              const timeDiff = now.getTime() - appointmentTime.getTime()
              const minutesDiff = timeDiff / (1000 * 60)

              if (minutesDiff <= 10) {
                console.log('🎯 Cita reciente confirmada!')

                setAppointmentData({
                  complete: latestAppointment,
                  isComplete: true,
                  scheduledAt: latestAppointment.createdAt,
                })

                // Complete the step with full data
                if (onComplete) {
                  await onComplete({
                    completedAt: new Date().toISOString(),
                    data: {
                      appointmentId: latestAppointment.id,
                      calendlyEventId: latestAppointment.calendlyEventId,
                      appointmentDate: latestAppointment.startTime,
                      endTime: latestAppointment.endTime,
                      appointmentType: latestAppointment.appointmentType,
                      status: latestAppointment.status,
                      contactName: latestAppointment.userName || userInfo.name,
                      contactEmail:
                        latestAppointment.userEmail || userInfo.email,
                      location:
                        latestAppointment.meetingLocation ||
                        userInfo.primaryAddress,
                      timezone: latestAppointment.timezone,
                      eventName: latestAppointment.eventName,
                    },
                  })
                }

                toast.success('¡Cita confirmada y datos obtenidos!')
                return latestAppointment
              }
            }
          }
        } catch (error) {
          console.warn(`⚠️ Error en intento ${attempt}:`, error)
        }
      }

      console.log('⏰ No se encontró cita reciente')
      toast('Cita programada. Los detalles se confirmarán por email.', {
        icon: 'ℹ️',
      })

      // Complete with basic data if we don't find the appointment
      if (onComplete) {
        await onComplete({
          completedAt: new Date().toISOString(),
          data: {
            appointmentType: 'visita_ocular', // Default fallback
            contactName: userInfo.name,
            contactEmail: userInfo.email,
            location: userInfo.primaryAddress,
            status: 'scheduled',
            scheduledVia: 'calendly',
          },
        })
      }

      return null
    },
    []
  )

  return {
    appointmentData,
    isPollingData,
    setAppointmentData,
    setIsPollingData,
    createManualAppointment,
    fetchAppointmentData,
  }
}
